import { Subject } from 'rxjs';
import { RxReplicationPullStreamItem, WithDeleted, RxCollection } from 'rxdb';
import { replicateRxCollection, RxReplicationState } from 'rxdb/plugins/replication';
import { QueryClient } from '@tanstack/react-query';
import { Database, ObjectStores } from './types';
import { SurveyResponse } from 'types/surveyResponse';
import { Checkpoint } from 'types/dataPullPush';
import { QueryKeys } from 'types/queryKeys';
import { PendingBlob } from 'types/pendingBlob';
import { Survey } from 'types/survey';
import {
  pullSurveyDefinitions,
  pullSurveyResponses,
  pushSurveyResponses,
  putBlob,
  watchSurveyResponses
} from 'utils/api/backend';
import { base64ToBlob } from 'utils/blob';

export let surveyResponseReplicationState: RxReplicationState<SurveyResponse, Checkpoint>;
export let blobReplicationState: RxReplicationState<PendingBlob, Checkpoint>;
export let surveyDefinitionReplicationState: RxReplicationState<Survey, Checkpoint>;

// starts replication for application data between offline RxDB and backend
// for almost all data this should be the only place where server requests happen
export const initReplication = async (database: Database, queryClient: QueryClient) => {
  // already initialized?
  if (surveyResponseReplicationState) {
    return;
  }

  // survey responses
  // start real-time events
  const surveyResponseSubject = new Subject<RxReplicationPullStreamItem<SurveyResponse, Checkpoint>>();
  await watchSurveyResponses(
    (e) => surveyResponseSubject.next(e),
    () => surveyResponseSubject.next('RESYNC')
  );

  // gather required collections
  const surveyResponsesCollection = database.collections[ObjectStores.SubmittedSurveys];
  const blobsCollection = database.collections[ObjectStores.PendingBlobs];
  const surveyDefinitionsCollection = database.collections[ObjectStores.UnsubmittedSurveys];

  if (!surveyResponsesCollection || !blobsCollection || !surveyDefinitionsCollection) {
    console.error('One or more required RxDB collections are missing for replication.');
    return;
  }

  // cast to concrete expected document types (schema already enforces shape; this silences optional/never issues)
  const surveyResponsesCollectionTyped = surveyResponsesCollection as unknown as RxCollection<SurveyResponse, any>;
  const blobsCollectionTyped = blobsCollection as unknown as RxCollection<PendingBlob, any>;
  const surveyDefinitionsCollectionTyped = surveyDefinitionsCollection as unknown as RxCollection<Survey, any>;

  // pull/push
  surveyResponseReplicationState = replicateRxCollection({
    collection: surveyResponsesCollectionTyped,
    replicationIdentifier: 'survey-responses-http',
    pull: {
      handler: async (checkpointOrNull: Checkpoint | undefined) => {
        const updatedAt = checkpointOrNull ? checkpointOrNull.updatedAt : 0;
        const data = await pullSurveyResponses(updatedAt);
        return {
          documents: data.updates,
          checkpoint: { updatedAt: data.timestamp, id: data.lastId }
        };
      },
      stream$: surveyResponseSubject.asObservable()
    },
    push: {
      handler: async (changedRows) => await pushSurveyResponses(changedRows)
    }
  });
  surveyResponseReplicationState.error$.subscribe((e) =>
    console.error('Survey response replication error', e, e.parameters)
  );
  database.collections[ObjectStores.SubmittedSurveys]?.$?.subscribe(() =>
    queryClient.invalidateQueries({ queryKey: [QueryKeys.SURVEY_RESPONSES] })
  );

  // blob replication is special, it is push-only and meant to delete locally stored blobs after successful upload
  blobReplicationState = replicateRxCollection({
    collection: blobsCollectionTyped,
    replicationIdentifier: 'blobs-http',
    push: {
      handler: async (changedRows) => {
        const result: WithDeleted<PendingBlob>[] = [];
        for (const changedRow of changedRows) {
          const pendingBlob = changedRow.newDocumentState;
          if (!pendingBlob._deleted) {
            await putBlob(
              pendingBlob.category,
              pendingBlob.name,
              await base64ToBlob(pendingBlob.mimeType, pendingBlob.content)
            );
            // for every successfully uploaded blob, return it as a "conflict" with deleted=true
            // this will then mark it as deleted into local RxDB, and then it can be garbage-collected by
            // RxDB cleanup plugin
            result.push({ ...pendingBlob, _deleted: true });
          }
        }
        return result;
      }
    }
  });
  blobReplicationState.error$.subscribe((e) => console.error('Blob replication error', e, e.parameters));

  // pull-only, non-real-time survey definition replication
  surveyDefinitionReplicationState = replicateRxCollection({
    collection: surveyDefinitionsCollectionTyped,
    replicationIdentifier: 'survey-definitions-http',
    pull: {
      handler: async (checkpointOrNull: Checkpoint | undefined) => {
        const updatedAt = checkpointOrNull ? checkpointOrNull.updatedAt : 0;
        const data = await pullSurveyDefinitions(updatedAt);
        return {
          documents: data.updates,
          checkpoint: { updatedAt: data.timestamp, id: data.lastId }
        };
      }
    }
  });
  surveyDefinitionReplicationState.error$.subscribe((e) =>
    console.error('Survey definition replication error', e, e.parameters)
  );
  database.collections[ObjectStores.UnsubmittedSurveys]?.$?.subscribe(() =>
    queryClient.invalidateQueries({ queryKey: [QueryKeys.SURVEY_DEFINITIONS] })
  );
};
